import { V2 } from "@ui/theme/DesignV2";
import { ActionButton, Avatar, DS, DetailRow as KitRow, EntityHeader, Section, FormIntro, Glyph } from "../ds/Kit";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { Typo } from "@ui/text/Typo";
import { useId, useState, type FC, type ReactNode } from "react";
import Input from "@ui/input/Input";
import { toast } from "react-toastify";
import { DEFAULT_CURRENCY } from "src/constants";
import { GD } from "src/GD";
import { amountValidationError, parsePositiveAmountToMinorUnits } from "src/utils/amounts";
import { getFriendlyErrorMessage } from "src/utils/errors";
import { FormParser } from "src/view/FormParser";
import { useIssuer } from "../issuer/IssuerProvider";
import { StatusBadge } from "./StatusBadge";
import { backUniversalDialog, S_UNIVERSAL_DIALOG } from "./universalDialog/signals";

interface CompanyDetailsProps {
	company: ICompanyRaw;
	onUpdate?: () => void;
}

const COMPANY_MOBILE = "@media (max-width: 620px)";
const COMPANY_BRAND = V2.ink;
const COMPANY_TEXT = V2.ink;
const COMPANY_MUTED = V2.muted;
const COMPANY_BORDER = V2.border;

const SectionTitle: FC<{ children: ReactNode }> = ({ children }) => (
	<Typo variant="headMedium" sx={{ color: COMPANY_TEXT, fontSize: "1rem", fontWeight: 750 }}>
		{children}
	</Typo>
);

const CompanyUserRow: FC<{ user: IUserSaved; onDisable: () => void }> = ({ user, onDisable }) => (
	<Box
		sx={{
			display: "grid",
			gridTemplateColumns: "32px minmax(0, 1fr) auto",
			alignItems: "center",
			gap: "12px",
			padding: "10px 0",
			backgroundColor: "#FFFFFF",
			borderBottom: `1px solid ${COMPANY_BORDER}`,
			borderRadius: 0,
			[COMPANY_MOBILE]: { gridTemplateColumns: "32px minmax(0, 1fr) auto", gap: "8px", padding: "10px 0" }
		}}
	>
		<Box
			sx={{
				width: "32px",
				height: "32px",
				borderRadius: "11px",
				backgroundColor: "#EEF3F6",
				border: "1px solid #D8E1E7",
				color: COMPANY_BRAND,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				fontSize: "0.82rem",
				fontWeight: 800,
				[COMPANY_MOBILE]: { width: "32px", height: "32px", borderRadius: "10px" }
			}}
		>
			{(user.name || "U")[0].toUpperCase()}
		</Box>
		<VBox gap="2px" sx={{ minWidth: 0 }}>
			<Typo variant="small" sx={{ color: COMPANY_TEXT, fontSize: "0.82rem", fontWeight: 750, lineHeight: 1.2, overflowWrap: "anywhere" }}>
				{user.name || "Unnamed user"}
			</Typo>
			<Typo variant="caption" sx={{ color: COMPANY_MUTED, fontSize: "0.7rem", lineHeight: 1.25, overflowWrap: "anywhere" }}>
				{user.email || "No e-mail"}
			</Typo>
			<HBox gap="8px" sx={{ alignItems: "center", flexWrap: "wrap" }}>
			<Typo variant="caption" sx={{ color: COMPANY_MUTED, fontSize: "0.7rem", lineHeight: 1.2, textTransform: "capitalize" }}>
				{String(user.company_role || "member").replaceAll("_", " ")}
			</Typo>
			<StatusBadge status={user.company_status} />
			</HBox>
		</VBox>
		<VBox gap="6px" sx={{ alignItems: "flex-end", flexShrink: 0 }}>
			{user.company_status !== "disabled" && (
				<Button
					variant="secondarySmall"
					ariaLabel={`Disable ${user.name || user.email || "user"}`}
					onClick={onDisable}
					sx={{ minWidth: "unset", minHeight: "34px", padding: "6px 12px", border: "1px solid #FECACA", borderRadius: "8px", backgroundColor: "#FFF7F7", color: "#B42318", fontSize: "0.68rem", fontWeight: 700, [COMPANY_MOBILE]: { minHeight: "44px" } }}
				>
					Disable
				</Button>
			)}
		</VBox>
	</Box>
);

export const CompanyDetails = ({ company, onUpdate }: CompanyDetailsProps) => {
	const [usersData, updateUsers] = GD.REQ_USERS.useRequest(undefined, undefined);
	const { selectedIssuer, refreshIssuers } = useIssuer();
	const issuerCurrency = selectedIssuer?.currency || DEFAULT_CURRENCY;
	const companyUsers = (usersData?.data || []).filter((user: IUserSaved) => user.company_uid === company.uid);
	const [usersExpanded, setUsersExpanded] = useState(false);
	const [usersSearch, setUsersSearch] = useState("");
	const [usersPage, setUsersPage] = useState(0);
	const usersPanelId = useId();
	const search = usersSearch.trim().toLowerCase();
	const filteredUsers = companyUsers.filter(user =>
		[user.name, user.email, String(user.company_role || "member").replaceAll("_", " ")]
			.some(value => value?.toLowerCase().includes(search))
	);
	const pageCount = Math.max(1, Math.ceil(filteredUsers.length / 5));
	const currentPage = Math.min(usersPage, pageCount - 1);
	const visibleUsers = filteredUsers.slice(currentPage * 5, currentPage * 5 + 5);

	const handleTopUp = () => {
		if (!selectedIssuer?.is_default) {
			toast.error("Legacy company top-up is available only for the default issuer account");
			return;
		}
		S_UNIVERSAL_DIALOG.invoke({
			label: "Top up company",
			size: "small",
			content: (
				<>
					<FormIntro icon={Glyph.money} title="Top up company balance" description="Move funds into the company balance used for cards and buyers." />
					<FormParser
						key="topUp"
						onSubmit={async data => {
							const parsedAmount = parsePositiveAmountToMinorUnits(data.amount);
							if (typeof parsedAmount.amount !== "number") return amountValidationError(parsedAmount.error || "Enter a valid amount.");
							const response = await GD.REQ_COMPANY_TOPUP.request({ amount: parsedAmount.amount, currency: issuerCurrency });
							if (response.error) {
								toast.error(getFriendlyErrorMessage(response.error, "Failed to top up company"));
								return response;
							}
							await refreshIssuers(true);
							toast.success("Top up successful");
							onUpdate?.();
							backUniversalDialog();
							return response;
						}}
						onRequest={async () => {
							const template = (await GD.REQ_COMPANY_TOPUP_TEMPLATE.request()).data!;
							template.currency = issuerCurrency;
							return { ...template, $__issuer_hidden: true, $__issuer_label_hidden: true };
						}}
						beforeActions={<Typo variant="caption" sx={{ color: COMPANY_MUTED, lineHeight: 1.5 }}>Account: {selectedIssuer?.name || "Default issuer"} · {issuerCurrency}</Typo>}
						primaryButton={{ title: "Top Up" }}
						secondaryButton={{ title: "Cancel", onClick: backUniversalDialog }}
					/>
				</>
			),
			closeDialog: backUniversalDialog
		});
	};

	const handleEdit = () => {
		S_UNIVERSAL_DIALOG.invoke({
			label: "Edit company",
			content: (
				<FormParser
					key="editCompany"
					onSubmit={async data => {
						const payload = { ...data };
						// @ts-expect-error The backend template may include its read-only balance field.
						if ("balance" in payload) delete payload.balance;
						const response = await GD.REQ_COMPANY_UPDATE.request(payload);
						if (!response.error) {
							toast.success("Company details updated");
							onUpdate?.();
							backUniversalDialog();
						}
						return response;
					}}
					onRequest={async () => {
						const response = await GD.REQ_COMPANY_GET.request(company.uid);
						return { ...response.data!, $__balance_hidden: true, $__number_label: "Registration number", $__vat_number_label: "VAT number", $__type_label: "Account type", $__decorator: {} };
					}}
					primaryButton={{ title: "Save Company" }}
					secondaryButton={{ title: "Cancel", onClick: backUniversalDialog }}
				/>
			),
			closeDialog: backUniversalDialog
		});
	};

	const openDisableConfirmation = (user: IUserSaved) => {
		S_UNIVERSAL_DIALOG.invoke({
			label: "Disable user",
			size: "small",
			content: (
				<VBox gap="18px">
					<Typo variant="medium" sx={{ color: COMPANY_TEXT, lineHeight: 1.5 }}>
						Disable {user.name || user.email || "this user"}? They will lose company access.
					</Typo>
					<HBox gap="10px" sx={{ justifyContent: "flex-end", [COMPANY_MOBILE]: { flexDirection: "column-reverse" } }}>
						<Button variant="secondary" onClick={backUniversalDialog}>Cancel</Button>
						<Button
							variant="primary"
							sx={{ backgroundColor: "#B42318" }}
							onClick={async () => {
								const response = await GD.REQ_COMPANY_USER_DISABLE.request({ user_uid: user.uid });
								if (response.error) {
									toast.error(getFriendlyErrorMessage(response.error, "Failed to disable user"));
									return;
								}
								toast.success(`User ${user.name || user.email} disabled`);
								updateUsers();
								backUniversalDialog();
							}}
						>
							Disable user
						</Button>
					</HBox>
				</VBox>
			),
			closeDialog: backUniversalDialog
		});
	};

	const handleDeleteAccount = () => {
		S_UNIVERSAL_DIALOG.invoke({
			label: "Delete account",
			size: "small",
			content: (
				<VBox gap="18px">
					<Typo variant="medium" sx={{ color: COMPANY_MUTED, lineHeight: 1.5 }}>
						Permanently delete your user account? This action cannot be undone.
					</Typo>
					<HBox gap="10px" sx={{ justifyContent: "flex-end", [COMPANY_MOBILE]: { flexDirection: "column-reverse" } }}>
						<Button variant="secondary" onClick={backUniversalDialog}>Cancel</Button>
						<Button
							variant="primary"
							sx={{ backgroundColor: "#B42318" }}
							onClick={async () => {
								const response = await GD.REQ_USER_DELETE.request();
								if (!response.error) {
									S_UNIVERSAL_DIALOG.invoke(null);
									GD.S_AUTH_LOG_OUT.invoke();
								}
							}}
						>
							Delete account
						</Button>
					</HBox>
				</VBox>
			),
			closeDialog: backUniversalDialog
		});
	};

	return (
		<Box sx={{ minWidth: 0, padding: "16px", backgroundColor: "transparent" }}>
			<VBox gap="16px">
				<EntityHeader
					stacked
					leading={<Avatar name={company.name} size={52} />}
					title={company.name}
					meta={`Registered ${new Date(company.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} · ${selectedIssuer?.name || "Default issuer"} · ${issuerCurrency}`}
					actions={
						<VBox gap="8px" sx={{ width: "100%" }}>
							<HBox gap="8px" sx={{ justifyContent: "flex-end", flexWrap: "wrap" }}>
								{selectedIssuer?.is_default && <ActionButton onClick={handleEdit}>Edit</ActionButton>}
								<ActionButton tone="primary" disabled={!selectedIssuer?.is_default} onClick={handleTopUp}>Top up</ActionButton>
							</HBox>
							{!selectedIssuer?.is_default && <Typo sx={{ color: DS.muted, fontSize: "12px", lineHeight: 1.5, textAlign: "right" }}>Company top-up is available for the default issuer account.</Typo>}
						</VBox>
					}
				/>

				<Section title="Company details">
					<KitRow label="Name" value={company.name || "—"} />
					<KitRow label="Registration no." value={company.number || "—"} />
					<KitRow label="VAT no." value={company.vat_number || "—"} />
				</Section>

				<Box sx={{ padding: "6px 18px 10px", borderRadius: DS.radius, border: `1px solid ${DS.line}`, background: DS.surface, boxShadow: DS.shadow }}>
					<Box element="button" aria-expanded={usersExpanded} aria-controls={usersPanelId}
						onClick={() => setUsersExpanded(expanded => !expanded)}
						sx={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", minHeight: "44px", padding: "0", border: 0, backgroundColor: "transparent", color: COMPANY_TEXT, cursor: "pointer", fontFamily: "inherit", textAlign: "left", borderRadius: "6px", "&:hover": { backgroundColor: "#F8F9FC" }, "&:focus-visible": { outline: "2px solid #5B84AD", outlineOffset: "2px" } }}>
						<SectionTitle>Company users</SectionTitle>
						<Box element="span" sx={{ padding: "3px 9px", borderRadius: "999px", backgroundColor: "#EEF3F6", color: COMPANY_BRAND, fontSize: "0.75rem", fontWeight: 750 }}>{companyUsers.length}</Box>
						<Box element="span" sx={{ marginLeft: "auto", color: COMPANY_MUTED, fontSize: "0.75rem" }}>{usersExpanded ? "Hide" : "Show"}</Box>
						<Box element="span" aria-hidden="true" sx={{ display: "flex", color: DS.muted, transform: usersExpanded ? "rotate(180deg)" : "none", transition: "transform .2s" }}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg></Box>
					</Box>
					<Box id={usersPanelId} sx={{ display: usersExpanded ? "block" : "none" }}>
						{usersExpanded && <VBox gap="10px" sx={{ paddingTop: "8px" }}>
							<Input name="companyUsersSearch" type="search" size="small" label="Search company users" placeholder="Name, email or role" autoComplete="off" data={{ companyUsersSearch: usersSearch }} onChange={value => { setUsersSearch(value); setUsersPage(0); }} />
							{visibleUsers.length === 0 ? <Typo variant="caption" sx={{ padding: "16px 0", color: COMPANY_MUTED }}>{companyUsers.length === 0 ? "No users registered for this company" : "No matching users"}</Typo> : (
								<VBox gap="0px" sx={{ minWidth: 0 }}>{visibleUsers.map(user => <CompanyUserRow key={user.uid} user={user} onDisable={() => openDisableConfirmation(user)} />)}</VBox>
							)}
							<HBox gap="8px" sx={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", paddingBottom: "4px" }}>
								<Typo variant="caption" sx={{ color: COMPANY_MUTED, fontSize: "0.75rem" }}>{filteredUsers.length ? `${currentPage * 5 + 1}–${currentPage * 5 + visibleUsers.length} of ${filteredUsers.length}` : "0 users"}</Typo>
								<HBox gap="6px">
									<Button variant="secondarySmall" disabled={currentPage === 0} onClick={() => setUsersPage(currentPage - 1)} sx={{ padding: "6px 10px", [COMPANY_MOBILE]: { minHeight: "44px" } }}>Previous</Button>
									<Button variant="secondarySmall" disabled={currentPage >= pageCount - 1} onClick={() => setUsersPage(currentPage + 1)} sx={{ padding: "6px 10px", [COMPANY_MOBILE]: { minHeight: "44px" } }}>Next</Button>
								</HBox>
							</HBox>
						</VBox>}
					</Box>
				</Box>

				<Section title="System information">
					<KitRow label="Company UID" value={company.uid} mono copy={company.uid} />
					<KitRow label="Created" value={new Date(company.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} />
				</Section>

				<HBox sx={{ alignItems: "center", justifyContent: "space-between", gap: "16px", padding: "16px 18px", borderRadius: DS.radius, border: "1px solid #FECACA", background: "#FFF7F7", [COMPANY_MOBILE]: { alignItems: "flex-start", flexDirection: "column" } }}>
					<VBox gap="3px" sx={{ minWidth: 0 }}>
						<Typo sx={{ color: "#991B1B", fontSize: "15px", fontWeight: 650 }}>Danger zone</Typo>
						<Typo sx={{ color: "#B91C1C", fontSize: "13px", lineHeight: 1.45 }}>Permanently delete your user account. This cannot be undone.</Typo>
					</VBox>
					<ActionButton tone="danger" onClick={handleDeleteAccount}>Delete account</ActionButton>
				</HBox>
			</VBox>
		</Box>
	);
};
